/**
 * client/src/lib/outbox.ts — the writes made while offline, and the runtime that drains them.
 *
 * §5.8's outbox: reading positions and "Sounds wrong" reports written on a train queue in IndexedDB
 * and flush on reconnect. This file also boots the rest of the offline runtime — the service worker
 * registration and the one flag the audio engine needs — because those three things share a single
 * question ("is the network there?") and answering it in three places is how they disagree.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════════
 * ⚠️ **POSITIONS ARE LAST-WRITE-WINS BY `clientUpdatedAt`, NEVER FURTHEST-WINS** (§7.6, LAW-adjacent
 * and stated three times in this repo because it is wrong in most reading apps).
 *
 * A reader who goes back to re-read chapter 2 has not *lost* their place at chapter 9; they have
 * **moved** it. A furthest-wins merge drags them forward every time they open the book, and it is
 * invisible: nothing errors, the book just refuses to let them go back. The outbox coalesces by
 * `(bookId, kind)` and keeps the row with the **greater `clientUpdatedAt`** — not the greater `ord`,
 * which is the same wrong answer wearing a queue.
 *
 * The server resolves the same way (`shared/position.ts`, `newerPosition`), so a queued write that
 * arrives after a newer write from a laptop is correctly ignored *there* too. The outbox never has
 * to guess: it sends what it has, stamped with when it happened.
 * ══════════════════════════════════════════════════════════════════════════════════════════════════
 *
 * ── What is in the queue, and what deliberately is not ───────────────────────────────────────────
 *
 *  · **`position`** — `PUT /api/v1/books/:id/position`. This is also §7.6's bookmark: the personal
 *    bookmark *is* a `positions` row (`kind: "furthest"`), and there is no second table. `enqueue`
 *    takes the wire body `PositionWriter` already builds, so there is exactly one spelling of that
 *    payload in the client.
 *  · **`report`** — `POST /api/v1/sentences/:sid/report`. "Sounds wrong", which §4.12 makes the one
 *    telemetry path an anonymous reader has. Losing it because a tunnel arrived first is losing the
 *    primary intake for the signal the whole lexicon is built from.
 *
 * There is no `note`/`highlight` kind, because there is no endpoint: margin notes are deferred past
 * P6 by name in §12. A queue entry for a route that answers 404 is a stub that pretends.
 *
 * ── Failure is a three-way decision, not a retry loop ────────────────────────────────────────────
 *
 *   transport failure (status 0) → the network is gone. **Stop the drain**, keep everything, and say
 *                                  so. Draining on into a dead network burns battery and reorders
 *                                  nothing usefully.
 *   4xx that is not 408/429      → the server has understood and refused. It will refuse the same
 *                                  bytes forever. **Drop the row** and record why, rather than
 *                                  retrying a 400 until the end of the world.
 *   5xx, 408, 429                → temporary. Keep, count the attempt, try again later.
 *
 * `MAX_ATTEMPTS` exists so that one poisoned row cannot block the ones behind it for ever. It is
 * generous, because the failure it guards against is rare and the data it discards is not.
 */

import {
  outboxAll,
  outboxDel,
  outboxPut,
  type OutboxRow,
} from "./idb.ts";
import { setNetworkOffline } from "../audio/pageWindow.ts";

// ─── what a queued write looks like ──────────────────────────────────────────

export interface Queued {
  kind: "position" | "report";
  /** The route to send it to, relative to the origin. Stored so a drain needs no re-derivation. */
  path: string;
  method: "PUT" | "POST";
  body: Record<string, unknown>;
  /** How many times we have tried and been told to try again later. */
  attempts: number;
  /** The last refusal, for the offline screen. Never shown as an error to a reader mid-sentence. */
  lastError: string | null;
  /** Only positions have one. It is what makes coalescing last-write-wins. */
  clientUpdatedAt?: number;
  /** Human-facing, for the offline screen's list. */
  label: string;
}

const MAX_ATTEMPTS = 12;

/** How long after a failed drain before trying again. Doubles, capped — a phone in a pocket. */
const RETRY_MIN_MS = 5_000;
const RETRY_MAX_MS = 5 * 60_000;

// ─── network state, in one place ─────────────────────────────────────────────

/**
 * `navigator.onLine` is trustworthy in exactly one direction.
 *
 * `false` means the OS is certain there is no route, and that is worth acting on immediately.
 * `true` means an interface is up — a captive portal, a hotel wifi that resolves DNS and refuses
 * everything else, a tailnet with no exit node — and says nothing about whether cloud-2 is
 * reachable. So the flag the rest of the app reads is `onLine === false` **or** "the last real
 * request failed at the transport layer", and it is cleared by a success, never by an event.
 */
let lastRequestFailed = false;

export function isOffline(): boolean {
  if (typeof navigator !== "undefined" && navigator.onLine === false) return true;
  return lastRequestFailed;
}

type Listener = () => void;
const listeners = new Set<Listener>();

function announce(): void {
  // The audio engine needs this before anything else does: with no network, a "urgent" load must
  // prime from the offline cache instead of assigning a network URL that will never answer.
  setNetworkOffline(isOffline());
  for (const fn of listeners) fn();
}

export function subscribeOffline(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// ─── the queue ───────────────────────────────────────────────────────────────

/**
 * Queue a reading position.
 *
 * `body` is `PositionWriter`'s own wire object — §10's exact field list — passed through rather than
 * rebuilt here, so the queued write and the live write can never diverge in shape. LAW 9 lives at
 * the far end of that object: `ord`, `ms` and `frac` are present or the server answers 400, and a
 * queue that "helpfully" filled a missing one would turn a 400 into a silently wrong place.
 */
export async function enqueuePosition(
  bookId: string,
  kind: string,
  clientUpdatedAt: number,
  body: Record<string, unknown>,
  label: string,
  /**
   * Why the live write failed. **This is the parameter that makes `isOffline()` mean anything on a
   * phone**, and its absence was a real defect — recorded here because "default: transport" reads
   * like a shrug and is not.
   *
   * `lastRequestFailed` used to be written in exactly three places, none of them this one: the two
   * `send()` call sites and the `online` event. A page turn with no network takes THIS path, so
   * `isOffline()` collapsed to `navigator.onLine === false` — and on a phone associated to a captive
   * portal, a dead AP or a cellular hole, `onLine` stays **true**. `setNetworkOffline(false)` then
   * persisted, `primeIfUnhurried` kept returning `null` for an urgent load, and the engine was handed
   * a network URL that would never answer: **a fully downloaded book that will not play**, which is
   * the exact failure that branch was added to prevent. `<OfflineNotice>` stayed hidden too.
   *
   * The gate could not see it: Playwright's `context.setOffline(true)` flips `navigator.onLine` to
   * false, so the one signal the code depended on is the one the harness supplies.
   *
   * Defaults to `"transport"` because every caller that cannot tell the difference is one that failed
   * to reach the server at all; a caller that KNOWS the server answered must say `"server"`.
   */
  cause: "transport" | "server" = "transport",
): Promise<void> {
  const key = `pos:${bookId}:${kind}`;
  const existing = await findRow(key);

  // ⚠️ Last write wins. See the header. `>=` and not `>` so a re-queue of the identical write
  // refreshes `attempts`; a strictly older write is dropped on the floor, which is correct — the
  // reader has since been somewhere else.
  if (existing && typeof existing.clientUpdatedAt === "number" && existing.clientUpdatedAt > clientUpdatedAt) {
    return;
  }

  await outboxPut({
    key,
    // The `at` of the *position*, not of the enqueue: drain order must be the order things happened
    // to the reader, and a re-queue must not jump the report they filed before it.
    at: clientUpdatedAt,
    value: {
      kind: "position",
      path: `/api/v1/books/${encodeURIComponent(bookId)}/position`,
      method: "PUT",
      body,
      attempts: existing?.attempts ?? 0,
      lastError: existing?.lastError ?? null,
      clientUpdatedAt,
      label,
    } satisfies Queued,
  });

  if (cause === "transport") {
    lastRequestFailed = true;
    // And schedule the drain. Without this the row sits in IndexedDB until the `online` event, a
    // visibility change or the next page turn — and on a captive portal the `online` event never
    // fires, because the interface never went down.
    void drainAndReschedule();
  }
  announce();
}

/**
 * Send a "Sounds wrong" report, or queue it.
 *
 * Returns `true` when it reached the server and `false` when it was queued, so the menu can say
 * *"Saved — it will be sent when you're back online"* rather than *"Reported — thanks"*. Telling
 * somebody their report landed when it is sitting in IndexedDB is the small lie that teaches people
 * their reports do nothing.
 */
export async function reportOrQueue(
  sid: string,
  body: { bookId: string; reason: string; note?: string; sessionId?: string },
): Promise<boolean> {
  const path = `/api/v1/sentences/${encodeURIComponent(sid)}/report`;
  const sent = await send("POST", path, body);
  if (sent.outcome === "ok") {
    lastRequestFailed = false;
    return true;
  }
  if (sent.outcome === "permanent") {
    /**
     * The server understood and refused these exact bytes, and will refuse them again tomorrow —
     * queueing it would be an infinite retry of a 400. It is also NOT a success, and saying
     * "Reported — thanks" here would be the lie this whole path exists to avoid: `409 no-actor`
     * means this browser has no reading session and the report really was lost. Throwing lands in
     * `SentenceMenu`'s existing catch, which already words a failed action honestly.
     *
     * Note what does NOT reach here: `spendReportToken`'s refusal is a deliberate **200** with
     * `recorded: false`, because the thousandth report of the evening adds no signal and answering
     * an error to somebody who tapped *Sounds wrong* teaches them not to tap it again. The row is
     * what is refused, never the person.
     */
    lastRequestFailed = false;
    throw new Error(`That report didn't go through (${sent.detail ?? "refused"}).`);
  }
  if (sent.outcome === "offline") lastRequestFailed = true;

  await outboxPut({
    // One row per report. They are not coalescable: two reports on one sentence are two opinions,
    // and §4.12's whole intake is a count of them.
    key: `rep:${sid}:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`,
    at: Date.now(),
    value: {
      kind: "report",
      path,
      method: "POST",
      body,
      attempts: 0,
      lastError: sent.detail,
      label: `“Sounds wrong” on ${sid}`,
    } satisfies Queued,
  });
  announce();
  return false;
}

async function findRow(key: string): Promise<Queued | null> {
  for (const row of await outboxAll()) if (row.key === key) return row.value as Queued;
  return null;
}

/** What is waiting, oldest first. The offline screen renders it; nothing else reads it. */
export async function pending(): Promise<{ key: string; at: number; item: Queued }[]> {
  return (await outboxAll()).map((row: OutboxRow) => ({ key: row.key, at: row.at, item: row.value as Queued }));
}

export async function pendingCount(): Promise<number> {
  return (await outboxAll()).length;
}

// ─── the drain ───────────────────────────────────────────────────────────────

type Outcome = { outcome: "ok" | "permanent" | "temporary" | "offline"; detail: string | null };

async function send(method: string, path: string, body: unknown): Promise<Outcome> {
  let res: Response;
  try {
    res = await fetch(path, {
      method,
      headers: { "content-type": "application/json" },
      // The session is the signed httpOnly `abk` cookie (§6.1). There is no header to attach.
      credentials: "same-origin",
      body: JSON.stringify(body),
    });
  } catch {
    return { outcome: "offline", detail: "the request never reached the server" };
  }
  if (res.ok) return { outcome: "ok", detail: null };
  if (res.status === 408 || res.status === 429 || res.status >= 500) {
    return { outcome: "temporary", detail: `HTTP ${res.status}` };
  }
  // A 400/404/409 is the server saying no to these exact bytes. It will keep saying no.
  return { outcome: "permanent", detail: `HTTP ${res.status}` };
}

let draining: Promise<number> | null = null;
let retryTimer: ReturnType<typeof setTimeout> | null = null;
let retryDelay = RETRY_MIN_MS;

/**
 * Drain the queue, oldest first. Returns how many rows were sent.
 *
 * One at a time and in order, deliberately. Firing forty queued positions in parallel at a server
 * that resolves them by `clientUpdatedAt` is harmless but pointless; firing forty queued *reports*
 * in parallel trips `spendReportToken` and the server answers `recorded: false` to most of them —
 * an honest 200 that quietly discards the row. Serial delivery with the drain stopping on the first
 * transport failure is both gentler and more correct.
 */
export function flushOutbox(): Promise<number> {
  if (draining) return draining;
  draining = (async () => {
    let sentCount = 0;
    for (const row of await outboxAll()) {
      const item = row.value as Queued;
      if (!item || typeof item.path !== "string") {
        // A row we cannot understand is a row written by a version of this file that no longer
        // exists. Dropping it is better than retrying it until the storage fills.
        await outboxDel(row.key);
        continue;
      }

      const result = await send(item.method, item.path, item.body);

      if (result.outcome === "ok") {
        await outboxDel(row.key);
        sentCount++;
        lastRequestFailed = false;
        continue;
      }
      if (result.outcome === "permanent") {
        await outboxDel(row.key);
        lastRequestFailed = false;
        continue;
      }
      if (result.outcome === "offline") {
        lastRequestFailed = true;
        // Stop. Everything behind this row is going to fail the same way, and the order matters.
        break;
      }

      // Temporary. Count it, keep it, and stop the drain — a 503 means the server is having a bad
      // moment, and the correct response to that is not to send it thirty-nine more requests.
      const attempts = item.attempts + 1;
      if (attempts >= MAX_ATTEMPTS) {
        await outboxDel(row.key);
      } else {
        await outboxPut({ key: row.key, at: row.at, value: { ...item, attempts, lastError: result.detail } });
      }
      break;
    }

    announce();
    return sentCount;
  })().finally(() => {
    draining = null;
  });
  return draining;
}

/** Try now; if anything is still queued, arrange to try again with a widening gap. */
async function drainAndReschedule(): Promise<void> {
  await flushOutbox();
  if (retryTimer) {
    clearTimeout(retryTimer);
    retryTimer = null;
  }
  const left = await pendingCount();
  if (left === 0) {
    retryDelay = RETRY_MIN_MS;
    return;
  }
  retryTimer = setTimeout(() => void drainAndReschedule(), retryDelay);
  retryDelay = Math.min(retryDelay * 2, RETRY_MAX_MS);
}

// ─── the runtime ─────────────────────────────────────────────────────────────

export interface ServiceWorkerState {
  supported: boolean;
  /** A worker is controlling this page, so the shell and book caches are live. */
  controlling: boolean;
  /** A new build is precached and waiting. `applyUpdate()` swaps to it. */
  updateReady: boolean;
  build: string | null;
}

let swState: ServiceWorkerState = {
  supported: typeof navigator !== "undefined" && "serviceWorker" in navigator,
  controlling: false,
  updateReady: false,
  build: null,
};

const swListeners = new Set<Listener>();

export function serviceWorkerState(): ServiceWorkerState {
  return swState;
}

export function subscribeServiceWorker(fn: Listener): () => void {
  swListeners.add(fn);
  return () => swListeners.delete(fn);
}

function setSwState(patch: Partial<ServiceWorkerState>): void {
  swState = { ...swState, ...patch };
  for (const fn of swListeners) fn();
}

/**
 * Swap to the waiting build and reload.
 *
 * The only caller of `skipWaiting` in the product — `client/src/sw.ts` deliberately never calls it
 * on its own, because activating a new worker under a live reader deletes the shell cache the open
 * tab is still lazily importing chunks from, which is the blank page the precache exists to prevent.
 * Here it is a deliberate tap, and the reload immediately afterwards is what makes it safe.
 */
export async function applyUpdate(): Promise<void> {
  if (!swState.supported) return;
  const reg = await navigator.serviceWorker.getRegistration();
  const waiting = reg?.waiting;
  if (!waiting) {
    location.reload();
    return;
  }
  let reloaded = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (reloaded) return;
    reloaded = true;
    location.reload();
  });
  waiting.postMessage({ type: "ab-skip-waiting" });
}

let started = false;

/**
 * Start the offline runtime. Called once, from `client/src/main.tsx`, above React.
 *
 * Registration is `import.meta.env.PROD` only. In dev, Vite serves modules from `/src/…` with no
 * `dist/sw.js` to register — the request would fall through the SPA handler and the browser would
 * refuse `index.html` as a service worker for its MIME type, which is the same blank-page shape
 * `vite.config.ts` records for the `"/s"` proxy prefix and just as hard to see. A stale service
 * worker in front of a hot-reloading dev server is also its own afternoon.
 */
export function startOfflineRuntime(): void {
  if (started || typeof window === "undefined") return;
  started = true;

  addEventListener("online", () => {
    lastRequestFailed = false;
    announce();
    void drainAndReschedule();
  });
  addEventListener("offline", () => {
    announce();
  });

  // A phone that was locked in a pocket for an hour runs no timers. `visible` is the moment we are
  // certain time has passed and the radio is awake, and it is the same signal §4.9 re-polls page
  // state on for exactly this reason.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void drainAndReschedule();
  });

  announce();
  void drainAndReschedule();

  if (!swState.supported || !import.meta.env.PROD) return;

  navigator.serviceWorker.addEventListener("message", (event: MessageEvent) => {
    const data = event.data as { type?: string; build?: string } | null;
    if (!data || typeof data.type !== "string") return;
    if (data.type === "ab-update-ready") setSwState({ updateReady: true });
    if (data.type === "ab-sw-active") setSwState({ controlling: true, build: data.build ?? null });
  });

  // `updateViaCache: "none"` because `@fastify/static` sets no `Cache-Control` on `/sw.js` (it
  // special-cases only `.html` and `/assets/`), so a heuristically-cached worker script is a deploy
  // that never reaches the browsers that most need it.
  void navigator.serviceWorker
    .register("/sw.js", { scope: "/", updateViaCache: "none" })
    .then((reg) => {
      setSwState({ controlling: navigator.serviceWorker.controller !== null });
      if (reg.waiting) setSwState({ updateReady: true });
      reg.addEventListener("updatefound", () => {
        const installing = reg.installing;
        if (!installing) return;
        installing.addEventListener("statechange", () => {
          // `installed` with a controller already present means this is an update, not a first run.
          if (installing.state === "installed" && navigator.serviceWorker.controller) {
            setSwState({ updateReady: true });
          }
        });
      });
    })
    .catch(() => {
      // A failed registration is not a failure of the app. Downloads still work (they write to the
      // Cache API directly, which needs no worker); only the offline *shell* is unavailable, and
      // `pages/Offline.tsx` says so rather than claiming a capability that is not there.
      setSwState({ supported: false });
    });
}
